import { useEffect, useState, useRef } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MapPin, Boxes, Wrench, Package, ImageOff, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Wallet, CreditCard } from "lucide-react";
import { MobileActionBar } from "../components/MobileActionBar";
import { Badge } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { Stars, RatingSummary, ReviewList } from "../components/Reviews";
import { peso } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ItemDetail() {
  const [, params] = useRoute("/item/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";

  const [item, setItem] = useState<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound">("loading");
  const [qty, setQty] = useState("1");
  const [rentDays, setRentDays] = useState("1");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [placed, setPlaced] = useState<any>(null);
  const [reviews, setReviews] = useState<{ average: number; count: number; breakdown: Record<string, number>; reviews: any[] } | null>(null);
  const [payMethod, setPayMethod] = useState<"wallet" | "credit">("wallet");
  const [credit, setCredit] = useState<{ available: number } | null>(null);
  const orderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setState("loading");
    api.getItem(id)
      .then((it) => {
        if (!alive) return;
        if (it) {
          setItem(it); setState("ready");
          if (it.ownerUserId) api.userReviews(it.ownerUserId).then((rv) => { if (alive) setReviews(rv); }).catch(() => {});
        } else setState("notfound");
      })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => { if (user) api.getCredit().then((c) => setCredit(c)).catch(() => setCredit(null)); }, [user?.id]);

  if (state === "loading") return <DetailLoading label="Loading item…" />;
  if (state === "notfound" || !item) return <DetailNotFound title="Item not found" backHref="/marketplace?tab=Materials" backLabel="Browse Materials" />;

  const isRent = item.mode === "rent";
  const isEquip = item.kind === "equipment";
  const isOwner = !!user && user.id === item.ownerUserId;
  const active = item.status === "active";
  const inStock = (item.stock ?? 0) > 0;
  const q = Math.max(1, Number(String(qty).replace(/[^\d]/g, "")) || 1);
  const days = Math.max(1, Number(String(rentDays).replace(/[^\d]/g, "")) || 1);
  const total = isRent ? item.price * q * days : item.price * q;
  const canOrder = active && inStock && !isOwner;

  async function placeOrder(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!user) { navigate("/login"); return; }
    if (q > item.stock) { setErr(`Only ${item.stock} available.`); return; }
    setBusy(true);
    try {
      const res = await api.placeOrder({ itemId: item.id, qty: q, rentDays: isRent ? days : undefined, paymentMethod: payMethod });
      if (res?.order) { setPlaced(res.order); setItem({ ...item, stock: item.stock - q }); }
      else setErr("Could not place the order.");
    } catch (e: any) { setErr(e?.message || "Could not place the order."); }
    finally { setBusy(false); }
  }

  const tags = Array.isArray(item.tags) ? item.tags : [];

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <Link href="/marketplace?tab=Materials" className="text-sm text-mut hover:text-navy">← Back to Materials</Link>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6 mt-3">
        {/* LEFT — item */}
        <div>
          <div className="card overflow-hidden">
            <div className="aspect-[16/10] bg-navy-50 grid place-items-center overflow-hidden">
              {item.imageUrl
                ? <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                : <div className="text-mut flex flex-col items-center gap-2"><ImageOff className="w-10 h-10" /><span className="text-xs">No photo provided</span></div>}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <span className={`chip ${isEquip ? "bg-brand-gold/15 text-brand-gold" : "bg-navy-50 text-navy-600"}`}>{isEquip ? <Wrench className="w-3 h-3" /> : <Boxes className="w-3 h-3" />}{isEquip ? "Equipment" : "Material"}</span>
                <span className={`chip ${isRent ? "bg-brand-green/10 text-brand-greendark" : "bg-navy-50 text-navy-600"}`}>{isRent ? "For Rent" : "For Sale"}</span>
                {item.condition && <span className="chip bg-navy-50 text-navy-600 capitalize">{item.condition}</span>}
              </div>
              <h1 className="text-2xl font-extrabold text-navy">{item.title}</h1>
              <p className="text-sm text-mut flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{item.location || "—"}{item.category ? ` · ${item.category}` : ""}</p>
              {item.description && <p className="text-navy-700 mt-4 whitespace-pre-line">{item.description}</p>}
              {tags.length > 0 && <div className="flex flex-wrap gap-1.5 mt-4">{tags.map((t: string) => <Badge key={t}>{t}</Badge>)}</div>}
              <div className="flex items-center gap-2 mt-5 pt-4 border-t border-line text-sm text-mut flex-wrap">
                <div className="w-8 h-8 rounded-full bg-navy-50 grid place-items-center text-navy font-bold text-xs">{(item.ownerName || "S").slice(0, 1)}</div>
                Sold by {item.ownerUserId
                  ? <Link href={`/u/${item.ownerUserId}`} className="font-semibold text-navy hover:text-brand-red">{item.ownerName || "Seller"}</Link>
                  : <span className="font-semibold text-navy">{item.ownerName || "Seller"}</span>}
                {item.sellerRating?.count > 0
                  ? <span className="inline-flex items-center gap-1"><Stars value={item.sellerRating.average} size={14} /><b className="text-navy">{item.sellerRating.average.toFixed(1)}</b>({item.sellerRating.count})</span>
                  : <span className="chip bg-navy-50 text-navy-600">New seller</span>}
              </div>
            </div>
          </div>

          {/* Seller reviews */}
          <div className="card p-5 mt-4">
            <h2 className="font-bold text-navy mb-3">Seller reviews</h2>
            {reviews ? (
              <>
                {reviews.count > 0 && <div className="mb-4"><RatingSummary average={reviews.average} count={reviews.count} breakdown={reviews.breakdown} /></div>}
                <ReviewList reviews={reviews.reviews} emptyText="This seller has no reviews yet — be the first after a completed order." />
              </>
            ) : <p className="text-sm text-mut">Loading reviews…</p>}
          </div>
        </div>

        {/* RIGHT — order panel */}
        <div ref={orderRef} className="lg:sticky lg:top-6 h-fit space-y-4">
          <div className="card p-5">
            <div className="text-3xl font-extrabold text-navy">{peso(item.price)}{isRent && <span className="text-base font-normal text-mut">/{item.unit || "day"}</span>}</div>
            <div className={`text-sm mt-1 ${inStock ? "text-brand-green" : "text-brand-red"}`}>{inStock ? `${item.stock} ${item.unit || "unit(s)"} available` : "Out of stock"}</div>

            {placed ? (
              <div className="mt-4 text-center py-4">
                <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
                <div className="font-bold text-navy">Order placed!</div>
                <p className="text-sm text-mut mt-1">{peso(placed.total)} is held in escrow. It's released to the seller once you confirm receipt.</p>
                <div className="flex gap-2 justify-center mt-4">
                  <Link href="/dashboard?tab=orders" className="btn-red">My Orders</Link>
                  <Link href="/marketplace?tab=Materials" className="btn-outline">Keep browsing</Link>
                </div>
              </div>
            ) : isOwner ? (
              <div className="mt-4 text-sm bg-navy-50 text-navy-700 rounded-lg p-3">
                This is your listing. Manage it from your <Link href="/dashboard?tab=items" className="underline font-semibold">dashboard</Link>.
              </div>
            ) : !active ? (
              <div className="mt-4 text-sm bg-navy-50 text-mut rounded-lg p-3">This listing is no longer available.</div>
            ) : (
              <form onSubmit={placeOrder} className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Quantity</label><input className="field" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} disabled={!inStock} /></div>
                  {isRent && <div><label className="label">Rental days</label><input className="field" inputMode="numeric" value={rentDays} onChange={(e) => setRentDays(e.target.value)} disabled={!inStock} /></div>}
                </div>
                {user && credit && credit.available > 0 && (
                  <div>
                    <label className="label">Payment</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setPayMethod("wallet")} className={`h-10 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 ${payMethod === "wallet" ? "border-navy-500 bg-navy-50 text-navy" : "border-line text-mut"}`}><Wallet className="w-3.5 h-3.5" />Wallet</button>
                      <button type="button" onClick={() => setPayMethod("credit")} className={`h-10 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 ${payMethod === "credit" ? "border-navy-500 bg-navy-50 text-navy" : "border-line text-mut"}`}><CreditCard className="w-3.5 h-3.5" />Credit</button>
                    </div>
                    {payMethod === "credit" && <p className="text-[11px] text-mut mt-1">{peso(credit.available)} credit available — repay from your wallet anytime.</p>}
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-line pt-3">
                  <span className="text-sm text-mut">Total{isRent ? ` (${q}×${days} day${days === 1 ? "" : "s"})` : ""}</span>
                  <span className="text-xl font-extrabold text-navy">{peso(total)}</span>
                </div>
                {err && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}
                <button type="submit" disabled={busy || !canOrder} className="btn-red w-full disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                  {inStock ? (isRent ? "Rent now" : "Buy now") : "Out of stock"}
                </button>
                {!user && <p className="text-xs text-mut text-center">You'll be asked to sign in first.</p>}
                {user && <p className="text-xs text-mut flex items-center gap-1 justify-center">{payMethod === "credit" ? <><CreditCard className="w-3 h-3" />Charged to your credit line</> : <><Wallet className="w-3 h-3" />Paid from your wallet balance</>}</p>}
              </form>
            )}
          </div>

          <div className="card p-4 text-xs text-mut flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
            <span>Payments are held in <span className="font-semibold text-navy">escrow</span> and only released to the seller after you confirm the item was received. Cancel before that for a full refund.</span>
          </div>
        </div>
      </div>

      {canOrder && !placed && (
        <MobileActionBar>
          <div className="shrink-0">
            <div className="text-[11px] text-mut leading-none">Total</div>
            <div className="text-base font-extrabold text-navy leading-tight">{peso(total)}</div>
          </div>
          <button type="button" onClick={() => orderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="btn-red flex-1"><Package className="w-4 h-4" />{isRent ? "Rent now" : "Buy now"}</button>
        </MobileActionBar>
      )}
    </div>
  );
}
